import { useEffect, useState, useCallback } from 'react';
import { useAppStore } from '@/store/appStore';
import { useAuthStore } from '@/store/authStore';
import { Users, UserPlus, Mail, Shield, Building2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const ROLE_COLORS = {
  admin: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  project_manager: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  member: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
  manager: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  editor: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  viewer: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
};

export default function PeoplePage() {
  const { user } = useAuthStore();
  const { currentOrg, projects, fetchProjects, fetchOrgMembers, addOrgMember, fetchProjectMembers, addProjectMember } = useAppStore();

  const [orgMembers, setOrgMembers] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectMembers, setProjectMembers] = useState([]);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('member');
  const [addingTo, setAddingTo] = useState('org'); // 'org' or 'project'
  const [isLoading, setIsLoading] = useState(false);

  const loadOrgMembers = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const members = await fetchOrgMembers(currentOrg.id);
      setOrgMembers(members);
    } catch (err) {
      console.error('Failed to load org members:', err);
    }
  }, [currentOrg, fetchOrgMembers]);

  const loadProjectMembers = useCallback(async () => {
    if (!selectedProject) return;
    try {
      const members = await fetchProjectMembers(selectedProject.id);
      setProjectMembers(members);
    } catch (err) {
      console.error('Failed to load project members:', err);
    }
  }, [selectedProject, fetchProjectMembers]);

  useEffect(() => {
    if (currentOrg) {
      fetchProjects(currentOrg.id);
      loadOrgMembers();
    }
  }, [currentOrg, fetchProjects, loadOrgMembers]);

  useEffect(() => {
    if (selectedProject) {
      loadProjectMembers();
    }
  }, [selectedProject, loadProjectMembers]);

  const handleAddMember = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (addingTo === 'org') {
        await addOrgMember(currentOrg.id, newMemberEmail, newMemberRole);
        await loadOrgMembers();
        toast.success('Member added to organization!');
      } else {
        await addProjectMember(selectedProject.id, newMemberEmail, newMemberRole);
        await loadProjectMembers();
        toast.success('Member added to project!');
      }
      setIsAddMemberOpen(false);
      setNewMemberEmail('');
      setNewMemberRole('member');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!currentOrg) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <p className="text-zinc-400">Select an organization to manage people</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="people-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Manrope']">People</h1>
          <p className="text-zinc-400 text-sm mt-1">Manage team members and permissions</p>
        </div>

        <Dialog open={isAddMemberOpen} onOpenChange={setIsAddMemberOpen}>
          <DialogTrigger asChild>
            <Button className="bg-cyan-500 text-black hover:bg-cyan-400" data-testid="add-member-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Add Member
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Add Member</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddMember} className="space-y-4 mt-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1.5">Add to</label>
                <Select value={addingTo} onValueChange={setAddingTo}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="add-to-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="org">Organization</SelectItem>
                    {selectedProject && <SelectItem value="project">Project: {selectedProject.name}</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1.5">Email Address</label>
                <Input
                  type="email"
                  value={newMemberEmail}
                  onChange={(e) => setNewMemberEmail(e.target.value)}
                  placeholder="member@example.com"
                  className="bg-zinc-950 border-zinc-800"
                  required
                  data-testid="member-email-input"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1.5">Role</label>
                <Select value={newMemberRole} onValueChange={setNewMemberRole}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="member-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {addingTo === 'org' ? (
                      <>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="project_manager">Project Manager</SelectItem>
                        <SelectItem value="member">Member</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="editor">Editor</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="submit"
                className="w-full bg-cyan-500 text-black hover:bg-cyan-400"
                data-testid="add-member-submit-btn"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Member'
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="organization" className="space-y-6">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="organization" className="data-[state=active]:bg-zinc-800" data-testid="org-tab">
            <Building2 className="w-4 h-4 mr-2" /> Organization
          </TabsTrigger>
          <TabsTrigger value="projects" className="data-[state=active]:bg-zinc-800" data-testid="projects-tab">
            <Users className="w-4 h-4 mr-2" /> Projects
          </TabsTrigger>
        </TabsList>

        <TabsContent value="organization" className="space-y-6">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="p-6 border-b border-zinc-800">
              <h3 className="text-lg font-semibold text-white">Organization Members</h3>
              <p className="text-sm text-zinc-400 mt-1">{orgMembers.length} members in {currentOrg.name}</p>
            </div>
            <div className="divide-y divide-zinc-800">
              {orgMembers.map((member) => (
                <div key={member.user_id} className="p-4 flex items-center justify-between hover:bg-zinc-800/30" data-testid={`org-member-${member.user_id}`}>
                  <div className="flex items-center gap-4">
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-zinc-700">
                        {member.user?.name?.charAt(0).toUpperCase() || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium text-white">
                        {member.user?.name}
                        {member.user_id === user?.id && <span className="text-zinc-500 ml-2">(You)</span>}
                      </p>
                      <p className="text-xs text-zinc-500 flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {member.user?.email}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className={ROLE_COLORS[member.role]}>
                    <Shield className="w-3 h-3 mr-1" />
                    {member.role.replace('_', ' ')}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="projects" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Project Selector */}
            <div className="md:col-span-1">
              <h3 className="text-sm font-medium text-zinc-400 mb-3">Select Project</h3>
              <div className="space-y-2">
                {projects.map((project) => (
                  <button
                    key={project.id}
                    onClick={() => setSelectedProject(project)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${selectedProject?.id === project.id
                      ? 'bg-zinc-800 border-cyan-500/50 text-white'
                      : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                      }`}
                    data-testid={`project-selector-${project.id}`}
                  >
                    {project.name}
                  </button>
                ))}
                {projects.length === 0 && (
                  <p className="text-sm text-zinc-500 text-center py-4">No projects yet</p>
                )}
              </div>
            </div>

            {/* Project Members */}
            <div className="md:col-span-3">
              {selectedProject ? (
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                  <div className="p-6 border-b border-zinc-800">
                    <h3 className="text-lg font-semibold text-white">{selectedProject.name} Members</h3>
                    <p className="text-sm text-zinc-400 mt-1">{projectMembers.length} members</p>
                  </div>
                  <div className="divide-y divide-zinc-800">
                    {projectMembers.map((member) => (
                      <div key={member.user_id} className="p-4 flex items-center justify-between hover:bg-zinc-800/30" data-testid={`project-member-${member.user_id}`}>
                        <div className="flex items-center gap-4">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="bg-zinc-700">
                              {member.user?.name?.charAt(0).toUpperCase() || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium text-white">{member.user?.name}</p>
                            <p className="text-xs text-zinc-500">{member.user?.email}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className={ROLE_COLORS[member.role]}>
                          {member.role}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-zinc-900/50 border border-zinc-800 border-dashed rounded-xl p-12 text-center">
                  <Users className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400">Select a project to view its members</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
